/**
 * Quiz - Handles multi-step quiz navigation and breadcrumb animations
 */

// Global state manager – accessible via window.OptimealQuizState
window.OptimealQuizState = window.OptimealQuizState || {
  currentStep: 1,
  familyQuantity: 1,
  activePetIndex: 0,
  pets: [{ name: "", answers: {}, formAnswers: {}, tags: [] }],
  globalTags: [],
};

/**
 * Build a flat Klaviyo properties object from quiz state alone.
 * Used in fallback/waitlist flows where OptimealResults is not yet populated.
 *
 * Example (1 pet, unsupported type selected):
 * { pet_count: 1, pet_1_name: "Whiskers", pet_1_tags: "cat, kitten", pet_1_food_usage: "Daily food" }
 *
 * Example (2 pets, one unsupported):
 * { pet_count: 2, pet_1_name: "Whiskers", pet_1_tags: "cat, kitten", pet_2_name: "Buddy", pet_2_tags: "dog, adult" }
 *
 * name/tags/weight_lb/activity stay explicit (same reasoning as
 * quiz-klaviyo.js); every other answered question is forwarded generically
 * as pet_N_<handle>, same mechanism as the other three Klaviyo flows.
 */
function buildFallbackKlaviyoProperties(petCount) {
  const statePets = window.OptimealQuizState?.pets || [];
  const properties = { pet_count: petCount };

  statePets.forEach((pet, i) => {
    const prefix = `pet_${i + 1}_`;
    if (pet.name) properties[`${prefix}name`] = pet.name;
    if (pet.tags?.length) properties[`${prefix}tags`] = pet.tags.join(", ");
    const weight = pet.formAnswers?.weight;
    if (weight != null) properties[`${prefix}weight_lb`] = weight;
    const activity = pet.formAnswers?.activity?.text;
    if (activity) properties[`${prefix}activity`] = activity;

    // Generic: every other answered question, keyed by its Shopify handle —
    // flattenAnswer/stripHtml are defined in quiz-klaviyo.js, loaded
    // alongside this file by quiz-modal.liquid before any waitlist
    // interaction can occur.
    Object.entries(pet.answersByHandle || {}).forEach(([handle, answer]) => {
      const value = flattenAnswer(answer);
      if (value) properties[`${prefix}${handle.replace(/-/g, "_")}`] = value;
    });
  });

  return properties;
}

class Quiz {
  constructor(element) {
    this.element = element;
    this.currentStep = 1;
    this.totalSteps = parseInt(element.dataset.totalSteps, 10) || 4;

    // Cache DOM elements
    this.breadcrumbs = element.querySelectorAll(".quiz__breadcrumb");
    this.breadcrumbLines = element.querySelectorAll(".quiz__breadcrumb-line");
    this.backButton = element.querySelector(".quiz__back");
    this.continueButton = element.querySelector(".quiz__continue");
    this.stepContent = element.querySelector(".quiz__step-content");
    this.isOnLastStepDisplay = false;
    this._suppressSave = false;
    this._waitlistShown = false;

    // Set up titles from global variable (set by Liquid)
    this.stepTitles =
      Object.fromEntries(
        Object.entries(window.quizSteps).map(([key, step]) => [
          key,
          step.title,
        ]),
      ) || {};

    this.init();
  }

  init() {
    // Set initial breadcrumb titles
    this.breadcrumbs.forEach((breadcrumb, index) => {
      const titleEl = breadcrumb.querySelector(".quiz__breadcrumb-title");
      if (titleEl) {
        titleEl.textContent = this.stepTitles[index + 1] || "";
      }
    });

    // Event listeners
    this.backButton?.addEventListener("click", () => this.prevStep());
    this.continueButton?.addEventListener("click", () => this.nextStep());

    // Restore saved progress if available, otherwise start fresh
    const saved = Quiz.loadProgress();
    if (saved) {
      window.OptimealQuizState = {
        currentStep: saved.currentStep || 1,
        familyQuantity: saved.familyQuantity || 1,
        activePetIndex: saved.activePetIndex || 0,
        pets: saved.pets || [
          { name: "", answers: {}, formAnswers: {}, tags: [] },
        ],
        globalTags: saved.globalTags || [],
        _familyQuantityAnswerIndex: saved._familyQuantityAnswerIndex ?? null,
      };
      if (saved.isOnLastStepDisplay) {
        // Sync currentStep so prevStep() lands on the correct last quiz step
        this.currentStep = saved.currentStep || this.totalSteps;
        this.renderLastStep();
      } else {
        this.goToStep(saved.currentStep || 1);
      }
    } else {
      this.goToStep(1);
    }
  }

  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > this.totalSteps) return;

    this.clearConfirmationCheckbox();
    this.continueButton?.classList.remove("quiz__continue--name-required");
    this.isOnLastStepDisplay = false;
    this.currentStep = stepNumber;
    window.OptimealQuizState.currentStep = stepNumber;

    // Update breadcrumbs
    this.breadcrumbs.forEach((breadcrumb, index) => {
      const step = index + 1;
      breadcrumb.classList.remove(
        "quiz__breadcrumb--active",
        "quiz__breadcrumb--completed",
      );

      if (step === this.currentStep) {
        breadcrumb.classList.add("quiz__breadcrumb--active");
      } else if (step < this.currentStep) {
        breadcrumb.classList.add("quiz__breadcrumb--completed");
      }
    });

    // Update connecting lines
    this.breadcrumbLines.forEach((line, index) => {
      line.classList.toggle(
        "quiz__breadcrumb-line--active",
        index + 1 < this.currentStep,
      );
    });

    // Restore footer visibility (waitlist may have hidden it)
    const footer = this.element.querySelector(".quiz__footer");
    if (footer) footer.classList.remove("hidden");

    // Reset waitlist flag before rendering — renderWaitlist() will set it if triggered
    this._waitlistShown = false;

    // Render step content based on display type
    this.stepRender(stepNumber);

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "quiz_step", {
        event_category: "quiz",
        step_number: stepNumber,
        step_title: this.stepTitles[stepNumber] || "",
      });
    }
    if (typeof fbq === "function") {
      fbq("trackCustom", "QuizStep", {
        step_number: stepNumber,
        step_title: this.stepTitles[stepNumber] || "",
      });
    }

    // Update buttons
    this.backButton?.classList.toggle("hidden", this.currentStep === 1);

    // Reset continue button text
    const arrowRight = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="18" viewBox="0 0 32 18" fill="none">
      <path d="M25 10H7C6.44 10 6 9.56 6 9C6 8.44 6.44 8 7 8H25C25.56 8 26 8.44 26 9C26 9.56 25.56 10 25 10Z" fill="white"/>
      <path d="M20 17.0009C19.8688 17.0025 19.7388 16.9767 19.6183 16.925C19.4977 16.8733 19.3893 16.797 19.3 16.7009C18.9 16.3009 18.9 15.6809 19.3 15.2809L25.6 8.98094L19.3 2.68094C18.9 2.28094 18.9 1.66094 19.3 1.26094C19.7 0.860938 20.32 0.860938 20.72 1.26094L27.72 8.26094C28.12 8.66094 28.12 9.28094 27.72 9.68094L20.72 16.6809C20.52 16.8809 20.26 16.9809 20.02 16.9809L20 17.0009Z" fill="white"/>
    </svg>`;
    this.continueButton.innerHTML = `Continue ${arrowRight}`;

    // Don't save progress if the waitlist was triggered — that state would always
    // reload straight back into the waitlist on the next session.
    if (!this._waitlistShown) {
      this.saveProgress();
    }
  }

  async nextStep() {
    // On step 2, if a name is still missing, show the required indicator instead of advancing
    if (
      this.continueButton?.classList.contains("quiz__continue--name-required")
    ) {
      const frameContainer = this.stepContent.querySelector(
        "[data-frame-container]",
      );
      frameContainer
        ?.querySelectorAll(".quiz-frame__pet-col")
        .forEach((col) => {
          const nameInput = col.querySelector(".quiz-frame__pet-name");
          if (nameInput && nameInput.value.trim() === "") {
            nameInput
              .closest(".quiz-frame__pet-name-row")
              ?.classList.add("quiz-frame__pet-name-row--error");
            nameInput.setAttribute("aria-invalid", "true");
            nameInput.focus();
          }
        });
      return;
    }

    // If we're on the last step display, capture email and fire quiz:complete
    if (this.isOnLastStepDisplay) {
      const emailInput = this.stepContent.querySelector(
        ".quiz-last__email-input",
      );
      const email = emailInput?.value?.trim() || "";
      const petCount = window.OptimealQuizState?.familyQuantity || 1;

      // Store for downstream listeners (quiz-klaviyo.js)
      window.OptimealResults = window.OptimealResults || {};
      window.OptimealResults.klaviyoEmail = email;
      window.OptimealResults.petCount = petCount;

      // Show loading spinner
      const spinnerTemplate = document.getElementById(
        "loading-spinner-template",
      );
      if (spinnerTemplate) {
        this.stepContent.innerHTML = "";
        this.stepContent.appendChild(spinnerTemplate.content.cloneNode(true));
      }
      this.setContinueEnabled(false);
      this.backButton?.classList.add("hidden");

      // Hide the footer
      const footer = this.element.querySelector(".quiz__footer");
      if (footer) footer.classList.add("hidden");

      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "quiz_complete", {
          event_category: "quiz",
          quiz_pet_count: petCount,
        });
      }
      if (typeof fbq === "function") {
        fbq("track", "Lead", { content_name: "quiz_complete", num_items: petCount });
      }
      document.dispatchEvent(new CustomEvent("quiz:complete"));
      return;
    }

    // Guard: for form step, ensure all pets have all fields filled
    if (this.isFormStep(this.currentStep)) {
      const formContainer = this.stepContent.querySelector(
        "[data-form-container]",
      );
      if (formContainer && !this.isFormComplete(formContainer)) return;
    }

    // Guard: for frame display, ensure all pets have answered
    const step = window.quizSteps[this.currentStep];
    const displayType = (step?.display || "simple").toLowerCase();
    if (displayType === "frame") {
      const frameContainer = this.stepContent.querySelector(
        "[data-frame-container]",
      );
      if (frameContainer) {
        const cols = frameContainer.querySelectorAll(".quiz-frame__pet-col");
        const allAnswered = Array.from(cols).every((col) =>
          col.querySelector(".quiz-answer--selected"),
        );
        if (!allAnswered) return;
      }
    }

    // Single-pet: if the active fallback has an email input, submit to Klaviyo first
    if (
      displayType === "frame" &&
      (window.OptimealQuizState?.familyQuantity || 1) === 1
    ) {
      const fallbackArea = this.stepContent.querySelector(
        "[data-fallback-area]:not(.hidden)",
      );
      const emailInput = fallbackArea?.querySelector("[data-fallback-email]");
      if (fallbackArea && emailInput) {
        const email = emailInput.value;
        const spinnerTemplate = document.getElementById(
          "loading-spinner-template",
        );
        if (spinnerTemplate) {
          fallbackArea.innerHTML = "";
          fallbackArea.appendChild(spinnerTemplate.content.cloneNode(true));
        }
        this.setContinueEnabled(false);

        const config = window.klaviyoConfig;
        if (config?.companyId && config?.listId) {
          const petCount = window.OptimealQuizState?.familyQuantity || 1;
          const properties = buildFallbackKlaviyoProperties(petCount);
          console.log("[quiz-fallback] Single-pet subscribing:", {
            email,
            properties,
            listId: config.listId,
          });
          try {
            const res = await subscribeToList(email, config.listId, properties);
            const responseText = await res.text();
            if (!res.ok) {
              console.error(
                "[quiz-fallback] Subscription error:",
                res.status,
                res.statusText,
                responseText,
              );
            } else {
              console.log(
                "[quiz-fallback] Subscribed successfully.",
                responseText,
              );
            }
          } catch (err) {
            console.error("[quiz-fallback] Failed to subscribe:", err);
          }
        }
        this.renderWaitlist();
        return;
      }
    }

    if (this.currentStep < this.totalSteps) {
      this.goToStep(this.currentStep + 1);
    } else {
      // All quiz steps answered — show last step display
      this.renderLastStep();
    }
  }

  prevStep() {
    if (this.isOnLastStepDisplay) {
      // Go back to the last quiz step
      this.goToStep(this.currentStep);
      return;
    }
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }

  // Enable/disable continue button (call from outside when validating answers)
  setContinueEnabled(enabled) {
    if (this.continueButton) {
      this.continueButton.disabled = !enabled;
    }
  }

  // Inserts the confirmation checkbox into the footer and disables Continue until checked
  showConfirmationCheckbox() {
    const footer = this.element.querySelector(".quiz__footer");
    if (!footer || footer.querySelector(".quiz__confirmation")) return;

    const label = document.createElement("label");
    label.className = "quiz__confirmation";
    label.innerHTML = `<input type="checkbox" class="quiz__confirmation-checkbox"><span>I understand and I would like to proceed.</span>`;

    this.setContinueEnabled(false);
    footer.insertBefore(label, this.continueButton);

    label.querySelector("input").addEventListener("change", (e) => {
      this.setContinueEnabled(e.target.checked);
    });
  }

  // Removes the confirmation checkbox from the footer
  clearConfirmationCheckbox() {
    this.element.querySelector(".quiz__footer .quiz__confirmation")?.remove();
  }

  // Step 1 is the family quantity question – structural, not a filtering answer
  static FAMILY_QUANTITY_STEP = 1;
  static STORAGE_KEY = "optimeal_quiz_progress";
  static STORAGE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  // Save an answer into the global state for the active pet
  saveAnswer(stepNumber, answerIndex) {
    const state = window.OptimealQuizState;
    const step = window.quizSteps[stepNumber];
    const answer = step?.answers?.[answerIndex];
    if (!answer) return;

    // Step 1 controls pet count – treat as structural, skip tag storage
    if (stepNumber === Quiz.FAMILY_QUANTITY_STEP) {
      state._familyQuantityAnswerIndex = answerIndex;
      this.initPetsFromQuantity(answer.text);
      return;
    }

    const pet = state.pets[state.activePetIndex];

    // Multiselect: toggle the answer in/out of a selections array
    if (step.type && step.type.toLowerCase() === "multiselect") {
      if (!pet.answers[stepNumber] || !pet.answers[stepNumber].multiselect) {
        pet.answers[stepNumber] = { multiselect: true, selections: [] };
      }
      const selections = pet.answers[stepNumber].selections;
      const existingIdx = selections.findIndex(
        (s) => s.answerIndex === answerIndex,
      );
      if (existingIdx >= 0) {
        selections.splice(existingIdx, 1);
      } else {
        selections.push({
          answerIndex,
          handle: answer.handle || null,
          tag: answer.tag || null,
          text: answer.text || "",
        });
      }
      if (step.handle) {
        pet.answersByHandle = pet.answersByHandle || {};
        pet.answersByHandle[step.handle] = pet.answers[stepNumber];
      }
      this.rebuildTags();
      return;
    }

    pet.answers[stepNumber] = {
      answerIndex,
      handle: answer.handle || null,
      tag: answer.tag || null,
      text: answer.text || "",
    };
    if (step.handle) {
      pet.answersByHandle = pet.answersByHandle || {};
      pet.answersByHandle[step.handle] = pet.answers[stepNumber];
    }

    // Mark pet as unsupported if the answer tag is exactly "Quiz - Unsupported"
    // Tag may be a string or an array (from metafields)
    const answerTags = Array.isArray(answer.tag) ? answer.tag : [answer.tag];
    if (answerTags.includes("Quiz - Unsupported")) {
      pet.unsupportedAtStep = stepNumber;
    } else if (pet.unsupportedAtStep === stepNumber) {
      // User changed their answer on this step — clear the flag
      delete pet.unsupportedAtStep;
    }

    this.rebuildTags();
  }

  // Parse quantity from answer text and create N pet objects
  initPetsFromQuantity(answerText) {
    const state = window.OptimealQuizState;
    const quantity = parseInt(answerText.replace(/<[^>]*>/g, ""), 10) || 1;

    state.familyQuantity = quantity;

    // Resize pets array: keep existing data where possible, add/trim as needed
    while (state.pets.length < quantity) {
      state.pets.push({ name: "", answers: {}, formAnswers: {}, tags: [] });
    }
    state.pets.length = quantity;

    // Reset active pet index if out of bounds
    if (state.activePetIndex >= quantity) {
      state.activePetIndex = 0;
    }

    this.saveProgress();
  }

  // Restore a previously saved answer for the current step (re-select UI)
  restoreAnswer(stepNumber) {
    const state = window.OptimealQuizState;

    // Step 1 is structural – restore from familyQuantity instead of pet answers
    if (stepNumber === Quiz.FAMILY_QUANTITY_STEP) {
      if (
        state.familyQuantity > 1 ||
        state._familyQuantityAnswerIndex != null
      ) {
        const container = this.stepContent.querySelector(
          "[data-answers-container]",
        );
        if (!container) return;
        const answer = container.querySelector(
          `[data-answer-index="${state._familyQuantityAnswerIndex}"]`,
        );
        if (answer) answer.click();
      }
      return;
    }

    const pet = state.pets[state.activePetIndex];
    const saved = pet?.answers?.[stepNumber];
    if (!saved) return;

    const container = this.stepContent.querySelector(
      "[data-answers-container]",
    );
    if (!container) return;

    // Multiselect: visually restore all selected answers without re-toggling state
    if (saved.multiselect) {
      saved.selections.forEach((sel) => {
        const answer = container.querySelector(
          `[data-answer-index="${sel.answerIndex}"]`,
        );
        if (answer) answer.classList.add("quiz-answer--selected");
      });
      if (saved.selections.length > 0) this.setContinueEnabled(true);
      return;
    }

    const answer = container.querySelector(
      `[data-answer-index="${saved.answerIndex}"]`,
    );
    if (answer) {
      answer.click();
    }
  }

  // Mirror a form answer into answersByHandle so it flows through the same
  // generic per-question map that inline/frame steps already populate —
  // this is what lets Klaviyo (and anything else) pick up any question by
  // its Shopify handle without a question-specific code change.
  setFormAnswerByHandle(petIndex, handle, value) {
    if (!handle) return;
    const state = window.OptimealQuizState;
    const pet = state.pets[petIndex];
    if (!pet.answersByHandle) pet.answersByHandle = {};
    pet.answersByHandle[handle] = value;
  }

  // Recalculate tags arrays from stored answers
  rebuildTags() {
    const state = window.OptimealQuizState;
    const allTags = new Set();

    // Tag values may be strings or arrays (from metafields) — always flatten
    const collectTags = (tag, petTags) => {
      const tags = Array.isArray(tag) ? tag : [tag];
      tags.forEach((t) => {
        petTags.push(t);
        allTags.add(t);
      });
    };

    state.pets.forEach((pet) => {
      const petTags = [];
      // Unsupported pets contribute no tags to filtering
      if (pet.unsupportedAtStep == null) {
        Object.values(pet.answers).forEach((ans) => {
          if (ans.multiselect) {
            ans.selections.forEach((sel) => {
              if (sel.tag) collectTags(sel.tag, petTags);
            });
          } else if (ans.tag) {
            collectTags(ans.tag, petTags);
          }
        });
        // Include tags from form answers
        if (pet.formAnswers) {
          Object.values(pet.formAnswers).forEach((ans) => {
            if (ans.tag) collectTags(ans.tag, petTags);
          });
        }
      }
      pet.tags = petTags;
    });

    state.globalTags = [...allTags];
    this.saveProgress();
  }

  // Return pets that are still active (not unsupported) for the given step.
  // Pets are only hidden on steps AFTER the one where they were marked unsupported.
  getActivePets(forStep) {
    const state = window.OptimealQuizState;
    return state.pets
      .map((pet, index) => ({ pet, index }))
      .filter(({ pet }) => {
        if (pet.unsupportedAtStep == null) return true;
        return forStep <= pet.unsupportedAtStep;
      });
  }

  // Reset quiz back to initial state (step 1, clean pets)
  resetQuiz() {
    Quiz.clearProgress();
    this._suppressSave = true;
    window.OptimealQuizState = {
      currentStep: 1,
      familyQuantity: 1,
      activePetIndex: 0,
      pets: [{ name: "", answers: {}, formAnswers: {}, tags: [] }],
      globalTags: [],
    };
    this.goToStep(1);
    this._suppressSave = false;
  }

  // Sync quiz navigation from external state changes
  syncStateToQuiz() {
    const state = window.OptimealQuizState;
    if (state.currentStep !== this.currentStep) {
      this.goToStep(state.currentStep);
    }
  }

  // Save current quiz state to localStorage for resume functionality
  saveProgress() {
    if (this._suppressSave) return;
    try {
      const state = window.OptimealQuizState;
      // Only persist if the user has made at least one selection
      const hasProgress =
        state._familyQuantityAnswerIndex != null ||
        state.currentStep > 1 ||
        state.pets.some(
          (p) =>
            Object.keys(p.answers || {}).length > 0 ||
            Object.keys(p.formAnswers || {}).length > 0,
        );
      if (!hasProgress) return;
      const data = {
        currentStep: state.currentStep,
        familyQuantity: state.familyQuantity,
        activePetIndex: state.activePetIndex,
        pets: state.pets,
        globalTags: state.globalTags,
        _familyQuantityAnswerIndex: state._familyQuantityAnswerIndex,
        isOnLastStepDisplay: this.isOnLastStepDisplay,
        savedAt: Date.now(),
      };
      localStorage.setItem(Quiz.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // localStorage unavailable (private browsing, quota, etc.)
    }
  }

  // Load saved quiz state from localStorage; returns null if expired or invalid
  static loadProgress() {
    try {
      const raw = localStorage.getItem(Quiz.STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data?.savedAt) return null;
      if (Date.now() - data.savedAt > Quiz.STORAGE_EXPIRY_MS) {
        localStorage.removeItem(Quiz.STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  // Clear saved progress (on complete or explicit abandon)
  static clearProgress() {
    try {
      localStorage.removeItem(Quiz.STORAGE_KEY);
    } catch (e) {}
  }

  // Check if the current step is the grouped form step
  isFormStep(stepNumber) {
    return (
      stepNumber === this.totalSteps &&
      window.quizFormQuestions &&
      window.quizFormQuestions.length > 0
    );
  }

  // Render step content based on display type
  stepRender(stepNumber) {
    // Handle grouped form step (last step when form questions exist)
    if (this.isFormStep(stepNumber)) {
      this.renderFormStep();
      return;
    }

    const step = window.quizSteps[stepNumber];
    if (!step) return;

    // Normalize display type to lowercase, default to 'simple'
    const displayType = (step.display || "simple").toLowerCase();
    const templateId = `quiz-template-${displayType}`;
    const template = document.getElementById(templateId);

    if (!template) {
      console.warn(`Quiz template not found: ${templateId}`);
      return;
    }

    // Clone template content
    const content = template.content.cloneNode(true);

    // Inject step-level data into data-field elements
    const titleEl = content.querySelector('[data-field="title"]');
    const textEl = content.querySelector('[data-field="text"]');
    const jsonEl = content.querySelector('[data-field="json"]');

    const activeCount = this.getActivePets(stepNumber).length;
    const useSingular = activeCount === 1 && step.singular_title;
    if (titleEl)
      titleEl.textContent = useSingular
        ? step.singular_title
        : step.title || "";

    let displayText = step.text || "";
    if (useSingular && displayText) {
      const tmp = document.createElement("div");
      tmp.innerHTML = displayText;
      const h1 = tmp.querySelector("h1");
      if (h1) {
        h1.textContent = step.singular_title;
        displayText = tmp.innerHTML;
      }
    }
    if (textEl) textEl.innerHTML = displayText;
    if (jsonEl) jsonEl.textContent = JSON.stringify(step, null, 2);

    // Inject answers if present
    this.injectAnswers(content, step.answers || []);

    // Clear and append to container
    this.stepContent.innerHTML = "";
    this.stepContent.appendChild(content);

    // Initialize display-specific behavior
    this.initDisplayBehavior(displayType);
  }

  // Clone answer template for each answer and inject data
  injectAnswers(content, answers) {
    const answerTemplate = content.querySelector("[data-answer-template]");
    const container = content.querySelector("[data-answers-container]");

    if (!answerTemplate || !container || !answers.length) {
      // Remove template if present but no answers
      answerTemplate?.remove();
      return;
    }

    answers.forEach((answer, index) => {
      const answerContent = answerTemplate.content.cloneNode(true);

      // Inject answer data
      const textEl = answerContent.querySelector('[data-field="answer-text"]');
      const fallbackEl = answerContent.querySelector(
        '[data-field="answer-fallback"]',
      );

      if (textEl) textEl.innerHTML = answer.text || "";
      if (fallbackEl) fallbackEl.innerHTML = answer.fallback_text || "";

      // Handle images if container exists
      const imagesContainer = answerContent.querySelector(
        "[data-images-container]",
      );
      if (imagesContainer && answer.list_of_images?.length) {
        answer.list_of_images.forEach((img) => {
          const imgEl = document.createElement("img");
          imgEl.src = img.url;
          imgEl.alt = img.alt || "";
          imagesContainer.appendChild(imgEl);
        });
      }

      // Set answer index as data attribute on root element
      const answerRoot = answerContent.querySelector("[data-answer]");
      if (answerRoot) {
        answerRoot.dataset.answerIndex = index;
      }

      container.appendChild(answerContent);
    });

    // Remove the template element itself
    answerTemplate.remove();
  }

  // Initialize display-specific interactive behavior
  initDisplayBehavior(displayType) {
    const initMethod = `initDisplay${displayType.charAt(0).toUpperCase() + displayType.slice(1)}`;
    if (typeof this[initMethod] === "function") {
      this[initMethod]();
    }
  }

  // Frame display type initialization
  initDisplayFrame() {
    const container = this.stepContent.querySelector(".quiz-display--frame");
    if (!container) return;

    const state = window.OptimealQuizState;
    const step = window.quizSteps[this.currentStep];
    const answers = step?.answers || [];
    const frameContainer = container.querySelector("[data-frame-container]");
    if (!frameContainer || !answers.length) return;

    if (step?.handle) frameContainer.dataset.handle = step.handle;

    // The injected answers sit in a hidden container — use them as source data
    const injectedAnswers = container.querySelectorAll(
      "[data-answers-container] [data-answer]",
    );

    // Only build columns for active (non-unsupported) pets
    const activePets = this.getActivePets(this.currentStep);

    // If all pets are unsupported, show waitlist
    if (activePets.length === 0) {
      this.renderWaitlist();
      return;
    }

    // Build one column per active pet
    activePets.forEach(({ pet, index: petIndex }) => {
      const col = document.createElement("div");
      col.className = "quiz-frame__pet-col";
      col.dataset.petIndex = petIndex;

      // Editable pet name
      const nameRow = document.createElement("div");
      nameRow.className = "quiz-frame__pet-name-row";
      const nameInput = document.createElement("input");
      nameInput.type = "text";
      nameInput.className = "quiz-frame__pet-name";
      nameInput.value = pet.name || "";
      nameInput.placeholder = "Enter Cat's Name";
      nameInput.required = true;
      nameInput.setAttribute("aria-label", `Name for pet ${petIndex + 1}`);
      const namesLocked = this.currentStep > 2;
      if (this.currentStep === 2) {
        nameInput.setAttribute("aria-required", "true");
        nameInput.setAttribute(
          "aria-invalid",
          nameInput.value.trim() === "" ? "true" : "false",
        );
      }
      if (namesLocked) {
        nameInput.readOnly = true;
        nameInput.setAttribute("aria-readonly", "true");
      }
      nameInput.addEventListener("input", () => {
        state.pets[petIndex].name = nameInput.value;
        if (this.currentStep === 2) {
          const isFilled = nameInput.value.trim() !== "";
          nameInput.setAttribute("aria-invalid", isFilled ? "false" : "true");
          if (isFilled) {
            nameInput
              .closest(".quiz-frame__pet-name-row")
              ?.classList.remove("quiz-frame__pet-name-row--error");
          }
          this.checkFrameContinue(frameContainer);
        }
      });

      nameRow.appendChild(nameInput);

      // Edit icon — only shown on the name-entry step (step 2)
      if (!namesLocked) {
        const editIcon = document.createElement("span");
        editIcon.className = "quiz-frame__edit-icon";
        editIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M11.333 1.333a1.886 1.886 0 0 1 2.667 0l.667.667a1.886 1.886 0 0 1 0 2.667L5.333 14H2v-3.333l9.333-9.334ZM10 4l2 2" stroke="#027265" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
        nameRow.appendChild(editIcon);
      }
      col.appendChild(nameRow);

      // Cat image area (appended after answers below)
      const imageArea = document.createElement("div");
      imageArea.className = "quiz-frame__image-area";

      // Default image (shown when no answer is selected)
      if (step.default_image) {
        const defaultSet = document.createElement("div");
        defaultSet.className =
          "quiz-frame__image-set quiz-frame__image-set--active";
        defaultSet.dataset.defaultImage = "";
        const defaultImg = document.createElement("img");
        defaultImg.src = step.default_image;
        defaultImg.alt = "";
        defaultSet.appendChild(defaultImg);
        imageArea.appendChild(defaultSet);
      }

      // Answers container
      const isMultiselect = step?.type?.toLowerCase() === "multiselect";
      const answersDiv = document.createElement("div");
      const isLongList =
        answers.length > 7 && step?.handle !== "health-outcome";
      const isShortList = answers.length <= 2 && !isLongList;
      answersDiv.className = [
        "quiz-frame__answers",
        isMultiselect ? "quiz-frame__answers--multiselect" : "",
        isLongList ? "quiz-frame--long-list" : "",
        isShortList ? "quiz-frame--short-list" : "",
      ]
        .filter(Boolean)
        .join(" ");

      answers.forEach((answerData, answerIndex) => {
        const card = document.createElement("button");
        card.type = "button";
        card.className = "quiz-frame__answer-card";
        card.dataset.answer = "";
        card.dataset.answerIndex = answerIndex;

        // Answer text
        const textDiv = document.createElement("div");
        textDiv.className = "quiz-frame__answer-text";
        textDiv.innerHTML = answerData.text || "";
        card.appendChild(textDiv);

        // Ongoing subscription discount flag (food-usage answers only). Both the
        // percentage and the copy come from per-tier theme settings via
        // window.quizDiscountTags; {percentage} in the copy is replaced with that
        // tier's number. No percentage or empty copy hides the flag.
        const discountTier = window.quizDiscountTags?.[answerData.handle];
        if (discountTier?.pct && discountTier?.label) {
          const discountTag = document.createElement("span");
          discountTag.className = "quiz-frame__answer-discount-tag";
          discountTag.textContent = discountTier.label.replace(
            /\{percentage\}/g,
            discountTier.pct,
          );
          card.appendChild(discountTag);
        }

        // Store fallback_text and learn_more_url as data attributes for later use
        if (answerData.fallback_text) {
          card.dataset.fallbackText = answerData.fallback_text;
        }
        if (answerData.learn_more_url) {
          card.dataset.learnMoreUrl = answerData.learn_more_url;
        }

        // Build image set in the image area
        if (answerData.list_of_images?.length) {
          const imageSet = document.createElement("div");
          imageSet.className = "quiz-frame__image-set";
          imageSet.dataset.imageSetIndex = answerIndex;
          answerData.list_of_images.forEach((img) => {
            const imgEl = document.createElement("img");
            imgEl.src = img.url;
            imgEl.alt = img.alt || "";
            imageSet.appendChild(imgEl);
          });
          imageArea.appendChild(imageSet);
        }

        answersDiv.appendChild(card);
      });

      col.appendChild(answersDiv);

      // Fallback area (hidden by default)
      const fallbackArea = document.createElement("div");
      fallbackArea.className = "quiz-frame__fallback-area hidden";
      fallbackArea.dataset.fallbackArea = "";

      // Step 4: fallback above image. Other steps: image above fallback.
      if (this.currentStep === 4) {
        col.appendChild(fallbackArea);
        if (!isMultiselect) col.appendChild(imageArea);
      } else {
        if (!isMultiselect) col.appendChild(imageArea);
        col.appendChild(fallbackArea);
      }

      frameContainer.appendChild(col);

      // Click behavior for this column's answer cards
      const colCards = answersDiv.querySelectorAll(".quiz-frame__answer-card");
      colCards.forEach((card) => {
        card.addEventListener("click", () => {
          const idx = card.dataset.answerIndex;

          if (isMultiselect) {
            // Toggle selection — do not deselect others
            card.classList.toggle("quiz-answer--selected");

            // Show fallback for the first selected card that has fallback text
            const selectedWithFallback = answersDiv.querySelector(
              ".quiz-answer--selected[data-fallback-text]",
            );
            if (selectedWithFallback?.dataset.fallbackText) {
              fallbackArea.innerHTML = this.parseFallbackPlaceholders(
                selectedWithFallback.dataset.fallbackText,
              );
              fallbackArea.classList.remove("hidden");
              const learnMoreLink = fallbackArea.querySelector(
                "[data-fallback-learn-more]",
              );
              if (learnMoreLink && selectedWithFallback.dataset.learnMoreUrl) {
                learnMoreLink.href = selectedWithFallback.dataset.learnMoreUrl;
                learnMoreLink.target = "_blank";
              }
              this.initFallbackComponents(fallbackArea, petIndex);
            } else {
              fallbackArea.innerHTML = "";
              fallbackArea.classList.add("hidden");
            }
          } else {
            // Single-select: deselect all, select clicked
            colCards.forEach((c) =>
              c.classList.remove("quiz-answer--selected"),
            );
            card.classList.add("quiz-answer--selected");

            // Swap image
            const sets = imageArea.querySelectorAll(".quiz-frame__image-set");
            sets.forEach((s) => {
              s.classList.toggle(
                "quiz-frame__image-set--active",
                s.dataset.imageSetIndex === idx,
              );
            });

            // Handle fallback
            const fallbackHTML = card.dataset.fallbackText;
            if (fallbackHTML) {
              fallbackArea.innerHTML =
                this.parseFallbackPlaceholders(fallbackHTML);
              fallbackArea.classList.remove("hidden");
              const learnMoreLink = fallbackArea.querySelector(
                "[data-fallback-learn-more]",
              );
              if (learnMoreLink && card.dataset.learnMoreUrl) {
                learnMoreLink.href = card.dataset.learnMoreUrl;
                learnMoreLink.target = "_blank";
              }
              this.initFallbackComponents(fallbackArea, petIndex);
            } else {
              fallbackArea.innerHTML = "";
              fallbackArea.classList.add("hidden");
            }
          }

          // Save answer for this pet
          const prevPetIndex = state.activePetIndex;
          state.activePetIndex = petIndex;
          this.saveAnswer(this.currentStep, parseInt(idx, 10));
          state.activePetIndex = prevPetIndex;

          // Check if ALL pets have answered — enable continue only then
          this.checkFrameContinue(frameContainer);
        });
      });

      // Restore saved answer(s) for this pet
      const saved = pet.answers?.[this.currentStep];
      if (saved != null) {
        if (saved.multiselect) {
          // Restore each selected answer visually without re-toggling state
          saved.selections.forEach((sel) => {
            const savedCard = colCards[sel.answerIndex];
            if (savedCard) savedCard.classList.add("quiz-answer--selected");
          });
          // Restore fallback text if any restored selection has one
          const selectedWithFallback = answersDiv.querySelector(
            ".quiz-answer--selected[data-fallback-text]",
          );
          if (selectedWithFallback?.dataset.fallbackText) {
            fallbackArea.innerHTML = this.parseFallbackPlaceholders(
              selectedWithFallback.dataset.fallbackText,
            );
            fallbackArea.classList.remove("hidden");
            const learnMoreLink = fallbackArea.querySelector(
              "[data-fallback-learn-more]",
            );
            if (learnMoreLink && selectedWithFallback.dataset.learnMoreUrl) {
              learnMoreLink.href = selectedWithFallback.dataset.learnMoreUrl;
              learnMoreLink.target = "_blank";
            }
            this.initFallbackComponents(fallbackArea, petIndex);
          }
        } else if (saved.answerIndex != null) {
          const savedCard = colCards[saved.answerIndex];
          if (savedCard) savedCard.click();
        }
      } else if (step?.handle === "health-outcome") {
        // Preselect answer index 6 by default for the health-outcome step
        const defaultCard = colCards[6];
        if (defaultCard) defaultCard.click();
      } else if (this.currentStep === 4) {
        // Preselect the "usage-daily-food" answer by default on step 4
        const defaultIndex =
          step?.answers?.findIndex((a) => a.handle === "usage-daily-food") ??
          -1;
        if (defaultIndex !== -1 && colCards[defaultIndex])
          colCards[defaultIndex].click();
      }
    });

    // Remove the hidden injected answers container (no longer needed)
    const hiddenContainer = container.querySelector("[data-answers-container]");
    if (hiddenContainer) hiddenContainer.remove();

    // On mobile, convert columns to a Swiper slider
    if (window.innerWidth < 769 && frameContainer.children.length > 1) {
      const swiperEl = document.createElement("div");
      swiperEl.className = "swiper quiz-frame__swiper";

      const swiperWrapper = document.createElement("div");
      swiperWrapper.className = "swiper-wrapper";

      Array.from(frameContainer.children).forEach((col) => {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        slide.appendChild(col);
        swiperWrapper.appendChild(slide);
      });

      const pagination = document.createElement("div");
      pagination.className = "swiper-pagination";

      swiperEl.appendChild(swiperWrapper);
      swiperEl.appendChild(pagination);
      frameContainer.appendChild(swiperEl);

      const initFrameSwiper = () => {
        if (typeof Swiper === "undefined") {
          setTimeout(initFrameSwiper, 100);
          return;
        }
        new Swiper(swiperEl, {
          slidesPerView: 1.2,
          spaceBetween: 16,
          slidesOffsetBefore: 24,
          slidesOffsetAfter: 24,
          pagination: { el: pagination, clickable: true },
        });
      };
      initFrameSwiper();
    }

    // Disable continue until all pets have selected
    this.setContinueEnabled(false);
    this.checkFrameContinue(frameContainer);
  }

  // Check if all pet columns have a selected answer
  checkFrameContinue(frameContainer) {
    const cols = frameContainer.querySelectorAll(".quiz-frame__pet-col");
    const allAnswered = Array.from(cols).every((col) =>
      col.querySelector(".quiz-answer--selected"),
    );
    const allNamed = Array.from(cols).every((col) => {
      const nameInput = col.querySelector(".quiz-frame__pet-name");
      return !nameInput || nameInput.value.trim() !== "";
    });
    // On step 2: if answers are done but a name is missing, keep the button
    // clickable (not truly disabled) so clicking it can reveal the error indicator.
    const nameRequired = this.currentStep === 2 && allAnswered && !allNamed;
    if (nameRequired) {
      this.continueButton?.classList.add("quiz__continue--name-required");
      this.setContinueEnabled(true);
    } else {
      this.continueButton?.classList.remove("quiz__continue--name-required");
      this.setContinueEnabled(allAnswered);
    }

    // Single-pet: if the visible fallback has an email input, require a valid email
    if (allAnswered && (window.OptimealQuizState?.familyQuantity || 1) === 1) {
      const fallbackArea = frameContainer.querySelector(
        "[data-fallback-area]:not(.hidden)",
      );
      const emailInput = fallbackArea?.querySelector("[data-fallback-email]");
      if (emailInput) {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value);
        this.setContinueEnabled(isValid);
      }
    }

    // Multi-pet: if every pet selected a fallback (kitten) answer, disable continue — waitlist btn handles submission
    // Only applies on step 2
    if (
      this.currentStep === 2 &&
      allAnswered &&
      (window.OptimealQuizState?.familyQuantity || 1) > 1
    ) {
      const allFallback = Array.from(cols).every(
        (col) =>
          !col
            .querySelector("[data-fallback-area]")
            ?.classList.contains("hidden"),
      );
      if (allFallback) this.setContinueEnabled(false);
    }

    // Swap continue button text if all pets will be unsupported on the next step
    const arrowRight = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="18" viewBox="0 0 32 18" fill="none">
      <path d="M25 10H7C6.44 10 6 9.56 6 9C6 8.44 6.44 8 7 8H25C25.56 8 26 8.44 26 9C26 9.56 25.56 10 25 10Z" fill="white"/>
      <path d="M20 17.0009C19.8688 17.0025 19.7388 16.9767 19.6183 16.925C19.4977 16.8733 19.3893 16.797 19.3 16.7009C18.9 16.3009 18.9 15.6809 19.3 15.2809L25.6 8.98094L19.3 2.68094C18.9 2.28094 18.9 1.66094 19.3 1.26094C19.7 0.860938 20.32 0.860938 20.72 1.26094L27.72 8.26094C28.12 8.66094 28.12 9.28094 27.72 9.68094L20.72 16.6809C20.52 16.8809 20.26 16.9809 20.02 16.9809L20 17.0009Z" fill="white"/>
    </svg>`;
    const familyQuantity = window.OptimealQuizState?.familyQuantity || 1;
    const allFallbackSelected = Array.from(cols).every(
      (col) =>
        !col
          .querySelector("[data-fallback-area]")
          ?.classList.contains("hidden"),
    );
    const nextStepActivePets = this.getActivePets(this.currentStep + 1);
    // Relabel to "Join the waitlist" when:
    //   - 1 pet selected kitten on step 2 (continue acts as submit → waitlist)
    //   - multiple pets, none selected kitten, but all will be unsupported next step
    // Multi-pet all-kitten stays "Continue" (disabled) — the waitlist btn handles submission
    const shouldRelabel =
      allAnswered &&
      ((familyQuantity === 1 &&
        allFallbackSelected &&
        this.currentStep === 2) ||
        (familyQuantity > 1 &&
          !allFallbackSelected &&
          nextStepActivePets.length === 0));
    if (shouldRelabel) {
      const label =
        window.quizWaitlistSettings?.actionButtonLabel || "Join the waitlist";
      this.continueButton.innerHTML = `${label} ${arrowRight}`;
    } else {
      this.continueButton.innerHTML = `Continue ${arrowRight}`;
    }

    // Show confirmation checkbox if Continue is enabled and the selected answer requires it
    this.clearConfirmationCheckbox();
    if (!this.continueButton.disabled && !nameRequired) {
      const requiresConfirmation = Array.from(cols).some((col) => {
        const selected = col.querySelector(".quiz-answer--selected");
        if (!selected) return false;
        const idx = parseInt(selected.dataset.answerIndex, 10);
        return (
          window.quizSteps?.[this.currentStep]?.answers?.[idx]
            ?.required_confirmation === true
        );
      });
      if (requiresConfirmation) this.showConfirmationCheckbox();
    }
  }

  // Parse fallback_text HTML, replacing placeholders with component markup
  parseFallbackPlaceholders(html) {
    return html
      .replace(
        /\{\{email_input\}\}/g,
        '<input type="email" class="quiz-frame__email-input" placeholder="Enter your email" data-fallback-email />',
      )
      .replace(
        /\{\{waitlist_button\}\}/g,
        '<button type="button" class="quiz-frame__waitlist-btn small-button-styles" data-fallback-waitlist-btn>Join the waitlist</button>',
      )
      .replace(
        /\{\{learn_more\}\}/g,
        '<a href="/pages/why-optimeal" target="_blank" class="quiz-frame__learn-more" data-fallback-learn-more>Click here to learn more</a>',
      );
  }

  // Attach event handlers to fallback components after DOM insertion
  initFallbackComponents(fallbackArea, petIndex) {
    const emailInput = fallbackArea.querySelector("[data-fallback-email]");
    const waitlistBtn = fallbackArea.querySelector(
      "[data-fallback-waitlist-btn]",
    );
    const isSinglePet = (window.OptimealQuizState?.familyQuantity || 1) === 1;

    if (emailInput) {
      emailInput.addEventListener("input", () => {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value);
        emailInput.classList.toggle(
          "quiz-frame__email-input--error",
          emailInput.value.length > 0 && !isValid,
        );
        if (waitlistBtn) waitlistBtn.disabled = !isValid;
        // Single-pet: continue button acts as submit — track email validity
        if (isSinglePet) this.setContinueEnabled(isValid);
      });
    }

    if (waitlistBtn) {
      waitlistBtn.disabled = true;
      // Single-pet: hide the button — quiz__continue handles submission instead
      if (isSinglePet) waitlistBtn.classList.add("hidden");
      waitlistBtn.addEventListener("click", async () => {
        const email = emailInput?.value;
        if (!email) return;

        // Show loading spinner in fallback area
        const spinnerTemplate = document.getElementById(
          "loading-spinner-template",
        );
        if (spinnerTemplate) {
          fallbackArea.innerHTML = "";
          fallbackArea.appendChild(spinnerTemplate.content.cloneNode(true));
        }

        // Subscribe to Klaviyo
        const config = window.klaviyoConfig;
        if (config?.companyId && config?.listId) {
          const petCount = window.OptimealQuizState?.familyQuantity || 1;
          const properties = buildFallbackKlaviyoProperties(petCount);
          console.log("[quiz-fallback] Subscribing:", {
            email,
            properties,
            listId: config.listId,
          });
          try {
            const res = await subscribeToList(email, config.listId, properties);
            const responseText = await res.text();
            if (!res.ok) {
              console.error(
                "[quiz-fallback] Subscription error:",
                res.status,
                res.statusText,
              );
              console.error("[quiz-fallback] Response:", responseText);
            } else {
              console.log("[quiz-fallback] Subscribed successfully.");
              console.log("[quiz-fallback] Response:", responseText);
            }
          } catch (err) {
            console.error("[quiz-fallback] Failed to subscribe:", err);
          }
        } else {
          if (!config?.companyId)
            console.error("[quiz-fallback] Missing Klaviyo companyId.");
          if (!config?.listId)
            console.error("[quiz-fallback] Missing Klaviyo listId.");
        }

        // If every pet selected a fallback answer, navigate to full waitlist display
        const allCols = this.stepContent.querySelectorAll(
          ".quiz-frame__pet-col",
        );
        const allFallback = Array.from(allCols).every(
          (col) =>
            !col
              .querySelector("[data-fallback-area]")
              ?.classList.contains("hidden"),
        );
        if (allFallback) {
          this.renderWaitlist();
        } else {
          // Some pets still have normal answers — show success in the fallback areas
          const allFallbackAreas = this.stepContent.querySelectorAll(
            "[data-fallback-area]",
          );
          allFallbackAreas.forEach((area) => this.renderFallbackSuccess(area));
        }
      });
    }
  }

  // Replace fallback area content with waitlist settings (title, image, button)
  renderFallbackSuccess(fallbackArea) {
    const cfg = window.quizWaitlistSettings || {};
    fallbackArea.innerHTML = "";

    if (cfg.image) {
      const img = document.createElement("img");
      img.className = "quiz-frame__fallback-success-image";
      img.src = cfg.image;
      img.alt = cfg.title || "";
      fallbackArea.appendChild(img);
    }

    if (cfg.title) {
      const title = document.createElement("h2");
      title.className = "quiz-frame__fallback-success-title";
      title.textContent = cfg.title;
      fallbackArea.appendChild(title);
    }
  }

  // Render waitlist display when all pets are unsupported
  renderWaitlist() {
    // Signal to goToStep() that it should not persist this as a resumable state.
    // Saving currentStep at a point where all pets are unsupported would cause
    // the waitlist to appear immediately on every subsequent session load.
    this._waitlistShown = true;
    Quiz.clearProgress();

    const template = document.getElementById("quiz-template-waitlist");
    if (!template) return;

    const content = template.content.cloneNode(true);
    const cfg = window.quizWaitlistSettings || {};

    const titleEl = content.querySelector('[data-field="title"]');
    const imageEl = content.querySelector('[data-field="image"]');
    const buttonEl = content.querySelector('[data-field="button"]');

    if (titleEl) titleEl.textContent = cfg.title || "";
    if (imageEl && cfg.image) {
      imageEl.src = cfg.image;
      imageEl.alt = cfg.title || "";
    }
    if (buttonEl) {
      buttonEl.textContent = cfg.buttonLabel || "";
      buttonEl.addEventListener("click", (e) => {
        e.preventDefault();
        this.resetQuiz();
        window.dispatchEvent(new Event("quiz:close"));
      });
    }

    this.stepContent.innerHTML = "";
    this.stepContent.appendChild(content);

    // Hide the entire footer — nowhere to navigate
    const footer = this.element.querySelector(".quiz__footer");
    if (footer) footer.classList.add("hidden");

    // Keep breadcrumbs on the previous step (don't visually advance)
    const prevStep = this.currentStep - 1;
    this.breadcrumbs.forEach((breadcrumb, index) => {
      const step = index + 1;
      breadcrumb.classList.remove(
        "quiz__breadcrumb--active",
        "quiz__breadcrumb--completed",
      );
      if (step === prevStep) {
        breadcrumb.classList.add("quiz__breadcrumb--active");
      } else if (step < prevStep) {
        breadcrumb.classList.add("quiz__breadcrumb--completed");
      }
    });
    this.breadcrumbLines.forEach((line, index) => {
      line.classList.toggle(
        "quiz__breadcrumb-line--active",
        index + 1 < prevStep,
      );
    });
  }

  // Render the grouped form step (all form questions, per pet)
  renderFormStep() {
    const template = document.getElementById("quiz-template-form");
    if (!template) return;

    const content = template.content.cloneNode(true);
    const meta = window.quizFormMeta || {};

    // Inject header
    const titleEl = content.querySelector('[data-field="title"]');
    const textEl = content.querySelector('[data-field="text"]');
    if (titleEl) titleEl.textContent = meta.title || "";
    if (textEl) textEl.innerHTML = meta.text || "";

    this.stepContent.innerHTML = "";
    this.stepContent.appendChild(content);

    this.initDisplayForm();
  }

  // Form display type initialization — builds per-pet columns with all form questions
  initDisplayForm() {
    const container = this.stepContent.querySelector(".quiz-display--form");
    if (!container) return;

    const state = window.OptimealQuizState;
    const formQuestions = window.quizFormQuestions || [];
    const formContainer = container.querySelector("[data-form-container]");
    if (!formContainer || !formQuestions.length) return;

    // Only build columns for active (non-unsupported) pets
    const activePets = this.getActivePets(this.currentStep);

    // If all pets are unsupported, show waitlist
    if (activePets.length === 0) {
      this.renderWaitlist();
      return;
    }

    activePets.forEach(({ pet, index: petIndex }) => {
      const col = document.createElement("div");
      col.className = "quiz-form__pet-col";
      col.dataset.petIndex = petIndex;

      // Editable pet name
      const nameRow = document.createElement("div");
      nameRow.className = "quiz-form__pet-name-row";
      const nameInput = document.createElement("input");
      nameInput.type = "text";
      nameInput.className = "quiz-form__pet-name";
      nameInput.value = pet.name || "";
      nameInput.placeholder = "Enter Cat's Name";
      nameInput.required = true;
      nameInput.setAttribute("aria-label", `Name for pet ${petIndex + 1}`);
      nameInput.addEventListener("change", () => {
        state.pets[petIndex].name = nameInput.value;
      });
      nameInput.addEventListener("input", () => {
        state.pets[petIndex].name = nameInput.value;
        const formContainer = this.stepContent.querySelector(
          "[data-form-container]",
        );
        if (formContainer) this.checkFormContinue(formContainer);
      });

      // Edit icon
      const editIcon = document.createElement("span");
      editIcon.className = "quiz-form__edit-icon";
      editIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M11.333 1.333a1.886 1.886 0 0 1 2.667 0l.667.667a1.886 1.886 0 0 1 0 2.667L5.333 14H2v-3.333l9.333-9.334ZM10 4l2 2" stroke="#027265" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;
      nameRow.appendChild(nameInput);
      nameRow.appendChild(editIcon);
      col.appendChild(nameRow);

      // Render each form question
      formQuestions.forEach((question, qIndex) => {
        const group = document.createElement("div");
        group.className = "quiz-form__question-group";
        group.dataset.questionIndex = qIndex;

        // Question label
        const label = document.createElement("div");
        label.className = "quiz-form__label";
        label.textContent = question.title || "";
        group.appendChild(label);

        const type = (question.type || "options").toLowerCase();
        const answers = question.answers || [];
        // Named key for known questions, positional index for the rest
        const storageKey = question.title?.toLowerCase().includes("activity")
          ? "activity"
          : qIndex;
        // Weight (scale) and activity already surface as their own named
        // Klaviyo properties (pet_N_weight_lb / pet_N_activity) — skip
        // mirroring them into answersByHandle, or they'd double-emit under
        // their own handle too.
        const handle =
          type === "scale" || storageKey === "activity"
            ? null
            : question.handle || null;

        if (type === "input") {
          this.renderFormInput(group, question, answers, petIndex, storageKey, handle);
        } else if (type === "slider") {
          this.renderFormSlider(group, answers, petIndex, storageKey, handle);
        } else if (type === "scale") {
          this.renderFormScale(group, petIndex, qIndex, handle);
        } else {
          // Default: options grid (2 columns)
          this.renderFormOptions(group, answers, petIndex, storageKey, handle);
        }

        col.appendChild(group);
      });

      formContainer.appendChild(col);
    });

    // On mobile, convert columns to a Swiper slider
    if (window.innerWidth < 769 && formContainer.children.length > 1) {
      const swiperEl = document.createElement("div");
      swiperEl.className = "swiper quiz-form__swiper";

      const swiperWrapper = document.createElement("div");
      swiperWrapper.className = "swiper-wrapper";

      Array.from(formContainer.children).forEach((col) => {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        slide.appendChild(col);
        swiperWrapper.appendChild(slide);
      });

      const pagination = document.createElement("div");
      pagination.className = "swiper-pagination";

      swiperEl.appendChild(swiperWrapper);
      swiperEl.appendChild(pagination);
      formContainer.appendChild(swiperEl);

      const initFormSwiper = () => {
        if (typeof Swiper === "undefined") {
          setTimeout(initFormSwiper, 100);
          return;
        }
        new Swiper(swiperEl, {
          slidesPerView: 1.2,
          spaceBetween: 16,
          slidesOffsetBefore: 24,
          slidesOffsetAfter: 24,
          pagination: { el: pagination, clickable: true },
          noSwiping: true,
          noSwipingSelector: ".quiz-form__slider-container",
        });
      };
      initFormSwiper();
    }

    // Disable continue until all fields are filled
    this.setContinueEnabled(false);
    this.checkFormContinue(formContainer);
  }

  // Render select dropdown for a form question
  renderFormInput(group, question, answers, petIndex, qIndex, handle) {
    const state = window.OptimealQuizState;

    const select = document.createElement("select");
    select.className = "quiz-form__select";
    select.dataset.questionIndex = qIndex;

    // Add placeholder option
    const placeholderOpt = document.createElement("option");
    placeholderOpt.value = "";
    placeholderOpt.textContent = "Select an option";
    placeholderOpt.disabled = true;
    placeholderOpt.selected = true;
    select.appendChild(placeholderOpt);

    // Add answer options
    answers.forEach((answer, answerIndex) => {
      const opt = document.createElement("option");
      opt.value = answerIndex;
      opt.innerHTML = answer.text || "";
      opt.dataset.tag = answer.tag || "";
      select.appendChild(opt);
    });

    // Restore saved value
    const saved = state.pets[petIndex]?.formAnswers?.[qIndex];
    if (saved != null && saved.answerIndex != null) {
      select.value = saved.answerIndex;
    }

    const saveSelect = () => {
      const answerIndex = parseInt(select.value, 10);
      const answer = answers[answerIndex];

      if (!state.pets[petIndex].formAnswers)
        state.pets[petIndex].formAnswers = {};
      const value = {
        answerIndex,
        tag: answer?.tag || null,
        text: answer?.text || "",
      };
      state.pets[petIndex].formAnswers[qIndex] = value;
      this.setFormAnswerByHandle(petIndex, handle, value);
      this.rebuildTags();
      const formContainer = this.stepContent.querySelector(
        "[data-form-container]",
      );
      if (formContainer) this.checkFormContinue(formContainer);
    };

    select.addEventListener("change", saveSelect);
    group.appendChild(select);
  }

  // Render scale (number input + lb/kg unit selector) for a form question
  renderFormScale(group, petIndex, qIndex, handle) {
    const state = window.OptimealQuizState;

    const wrapper = document.createElement("div");
    wrapper.className = "quiz-form__scale";

    const numberInput = document.createElement("input");
    numberInput.type = "number";
    numberInput.className = "quiz-form__scale-input";
    numberInput.min = "0";
    numberInput.step = "1";
    numberInput.placeholder = "Enter weight";

    const unitSelect = document.createElement("select");
    unitSelect.className = "quiz-form__scale-unit";
    ["lb", "kg"].forEach((unit) => {
      const opt = document.createElement("option");
      opt.value = unit;
      opt.textContent = unit;
      unitSelect.appendChild(opt);
    });

    // Restore saved value (always stored in lb)
    const saved = state.pets[petIndex]?.formAnswers?.weight;
    if (saved != null) {
      numberInput.value = saved;
      unitSelect.value = "lb";
    }

    const saveScale = () => {
      const raw = parseFloat(numberInput.value);
      if (isNaN(raw) || raw <= 0) return;

      const unit = unitSelect.value;
      const lbValue =
        unit === "kg" ? parseFloat((raw * 2.20462).toFixed(1)) : raw;

      if (!state.pets[petIndex].formAnswers)
        state.pets[petIndex].formAnswers = {};
      state.pets[petIndex].formAnswers.weight = lbValue;
      this.setFormAnswerByHandle(petIndex, handle, {
        text: `${lbValue} lb`,
        tag: null,
      });

      this.rebuildTags();
      const formContainer = this.stepContent.querySelector(
        "[data-form-container]",
      );
      if (formContainer) this.checkFormContinue(formContainer);
    };

    numberInput.addEventListener("change", saveScale);
    unitSelect.addEventListener("change", saveScale);

    const divider = document.createElement("span");
    divider.className = "quiz-form__scale-divider";

    wrapper.appendChild(numberInput);
    wrapper.appendChild(divider);
    wrapper.appendChild(unitSelect);
    group.appendChild(wrapper);
  }

  // Render slider (range input) for a form question
  renderFormSlider(group, answers, petIndex, qIndex, handle) {
    const state = window.OptimealQuizState;
    const sliderContainer = document.createElement("div");
    sliderContainer.className = "quiz-form__slider-container";

    // Range input
    const slider = document.createElement("input");
    slider.type = "range";
    slider.className = "quiz-form__slider";
    slider.min = "0";
    slider.max = (answers.length - 1).toString();
    slider.step = "any";
    slider.value = Math.floor((answers.length - 1) / 2).toString();

    // Labels container
    const labelsContainer = document.createElement("div");
    labelsContainer.className = "quiz-form__slider-labels";

    answers.forEach((answer, answerIndex) => {
      const label = document.createElement("span");
      label.className = "quiz-form__slider-label";
      label.innerHTML = answer.text || "";
      labelsContainer.appendChild(label);
    });

    // Track wrapper with dots (built first so updateFill can reference dotsContainer)
    const trackWrapper = document.createElement("div");
    trackWrapper.className = "quiz-form__slider-track-wrapper";

    const dotsContainer = document.createElement("div");
    dotsContainer.className = "quiz-form__slider-dots";
    answers.forEach(() => {
      const dot = document.createElement("span");
      dot.className = "quiz-form__slider-dot";
      dotsContainer.appendChild(dot);
    });

    trackWrapper.appendChild(dotsContainer);
    trackWrapper.appendChild(slider);

    // Save the snapped value to state
    const saveSlider = () => {
      const answerIndex = Math.round(parseFloat(slider.value));
      const answer = answers[answerIndex];

      if (!state.pets[petIndex].formAnswers)
        state.pets[petIndex].formAnswers = {};
      const value = {
        answerIndex,
        tag: answer.tag || null,
        text: answer.text || "",
      };
      state.pets[petIndex].formAnswers[qIndex] = value;
      this.setFormAnswerByHandle(petIndex, handle, value);
      this.rebuildTags();
      const formContainer = this.stepContent.querySelector(
        "[data-form-container]",
      );
      if (formContainer) this.checkFormContinue(formContainer);
    };

    const dots = dotsContainer.querySelectorAll(".quiz-form__slider-dot");
    const updateActiveDot = () => {
      const idx = Math.round(parseFloat(slider.value));
      dots.forEach((dot, i) =>
        dot.classList.toggle("quiz-form__slider-dot--active", i === idx),
      );
    };
    // Show all dots while dragging
    slider.addEventListener("input", () => {
      dots.forEach((dot) =>
        dot.classList.remove("quiz-form__slider-dot--active"),
      );
    });

    // Thumb moves freely during drag; snap to nearest option on release
    slider.addEventListener("change", () => {
      slider.value = Math.round(parseFloat(slider.value)).toString();
      updateActiveDot();
      saveSlider();
    });

    // Restore saved selection
    const saved = state.pets[petIndex]?.formAnswers?.[qIndex];
    if (saved != null && saved.answerIndex != null) {
      slider.value = saved.answerIndex.toString();
    }

    // Pre-save current value (default or restored) so continue can enable
    // without requiring user interaction when the default position is acceptable
    if (!state.pets[petIndex].formAnswers)
      state.pets[petIndex].formAnswers = {};
    if (state.pets[petIndex].formAnswers[qIndex] == null) {
      const answerIndex = Math.round(parseFloat(slider.value));
      const answer = answers[answerIndex];
      const value = {
        answerIndex,
        tag: answer?.tag || null,
        text: answer?.text || "",
      };
      state.pets[petIndex].formAnswers[qIndex] = value;
      this.setFormAnswerByHandle(petIndex, handle, value);
    }

    updateActiveDot();

    sliderContainer.appendChild(trackWrapper);
    sliderContainer.appendChild(labelsContainer);
    group.appendChild(sliderContainer);
  }

  // Render options grid (2 columns) for a form question
  renderFormOptions(group, answers, petIndex, qIndex, handle) {
    const state = window.OptimealQuizState;
    const grid = document.createElement("div");
    grid.className = "quiz-form__options-grid";

    answers.forEach((answer, answerIndex) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "quiz-form__option";
      btn.dataset.answerIndex = answerIndex;
      btn.innerHTML = answer.text || "";

      btn.addEventListener("click", () => {
        grid
          .querySelectorAll(".quiz-form__option")
          .forEach((b) => b.classList.remove("quiz-form__option--selected"));
        btn.classList.add("quiz-form__option--selected");

        if (!state.pets[petIndex].formAnswers)
          state.pets[petIndex].formAnswers = {};
        const value = {
          answerIndex,
          tag: answer.tag || null,
          text: answer.text || "",
        };
        state.pets[petIndex].formAnswers[qIndex] = value;
        this.setFormAnswerByHandle(petIndex, handle, value);
        this.rebuildTags();
        const formContainer = this.stepContent.querySelector(
          "[data-form-container]",
        );
        if (formContainer) this.checkFormContinue(formContainer);
      });

      grid.appendChild(btn);
    });

    // Restore saved selection
    const saved = state.pets[petIndex]?.formAnswers?.[qIndex];
    if (saved != null && saved.answerIndex != null) {
      const savedBtn = grid.children[saved.answerIndex];
      if (savedBtn) savedBtn.classList.add("quiz-form__option--selected");
    }

    group.appendChild(grid);
  }

  // Check if all form fields across all pets are filled
  isFormComplete(formContainer) {
    const formQuestions = window.quizFormQuestions || [];

    const activePets = this.getActivePets(this.currentStep);
    return activePets.every(({ pet }) => {
      if (!pet.name?.trim()) return false;
      return formQuestions.every((q, qIndex) => {
        const type = (q.type || "options").toLowerCase();
        if (type === "scale") return pet.formAnswers?.weight != null;
        const key = q.title?.toLowerCase().includes("activity")
          ? "activity"
          : qIndex;
        const saved = pet.formAnswers?.[key];
        if (!saved) return false;
        return saved.answerIndex != null || saved.tag != null || saved.text;
      });
    });
  }

  // Enable/disable continue based on form completion
  checkFormContinue(formContainer) {
    this.setContinueEnabled(this.isFormComplete(formContainer));
  }

  // Simple display type initialization
  initDisplaySimple() {
    const container = this.stepContent.querySelector(".quiz-display--simple");
    if (!container) return;

    const answers = container.querySelectorAll("[data-answer]");
    const globalImagesContainer = container.querySelector(
      "[data-global-images-container]",
    );

    // Build image sets in the global container from hidden answer images
    answers.forEach((answer) => {
      const imagesContainer = answer.querySelector("[data-images-container]");
      const answerIndex = answer.dataset.answerIndex;

      if (globalImagesContainer) {
        const imageSet = document.createElement("div");
        imageSet.className = "quiz-display__image-set";
        imageSet.dataset.imageSetIndex = answerIndex;

        // Clone images to the global container with position classes
        if (imagesContainer) {
          const images = imagesContainer.querySelectorAll("img");
          images.forEach((img, imgIndex) => {
            const clonedImg = img.cloneNode(true);
            clonedImg.classList.add(`quiz-display__cat--${imgIndex}`);
            imageSet.appendChild(clonedImg);
          });
        }

        globalImagesContainer.appendChild(imageSet);
      }
    });

    // Detect multiselect vs single-select for this step
    const currentStep = window.quizSteps[this.currentStep];
    const isMultiselect = currentStep?.type?.toLowerCase() === "multiselect";

    if (isMultiselect) {
      // Multiselect: toggle each answer independently
      answers.forEach((answer) => {
        answer.addEventListener("click", () => {
          answer.classList.toggle("quiz-answer--selected");

          const selectedIndex = parseInt(answer.dataset.answerIndex, 10);
          this.saveAnswer(this.currentStep, selectedIndex);

          const anySelected = Array.from(answers).some((a) =>
            a.classList.contains("quiz-answer--selected"),
          );
          this.clearConfirmationCheckbox();
          if (anySelected) {
            const requiresConfirmation = Array.from(answers)
              .filter((a) => a.classList.contains("quiz-answer--selected"))
              .some((a) => {
                const idx = parseInt(a.dataset.answerIndex, 10);
                return (
                  window.quizSteps?.[this.currentStep]?.answers?.[idx]
                    ?.required_confirmation === true
                );
              });
            if (requiresConfirmation) {
              this.showConfirmationCheckbox();
            } else {
              this.setContinueEnabled(true);
            }
          } else {
            this.setContinueEnabled(false);
          }
        });
      });
    } else {
      // Single-select click behavior
      answers.forEach((answer) => {
        answer.addEventListener("click", () => {
          // Remove selected state from all answers
          answers.forEach((a) => a.classList.remove("quiz-answer--selected"));

          // Add selected state to clicked answer
          answer.classList.add("quiz-answer--selected");

          // Show corresponding image set, hide others
          const selectedIndex = answer.dataset.answerIndex;

          const imageSets = globalImagesContainer?.querySelectorAll(
            ".quiz-display__image-set",
          );
          imageSets?.forEach((set) => {
            set.classList.toggle(
              "quiz-display__image-set--active",
              set.dataset.imageSetIndex === selectedIndex,
            );
          });

          // Enable continue (or show confirmation if this answer requires it)
          this.clearConfirmationCheckbox();
          const answerData =
            window.quizSteps?.[this.currentStep]?.answers?.[
              parseInt(selectedIndex, 10)
            ];
          if (answerData?.required_confirmation) {
            this.showConfirmationCheckbox();
          } else {
            this.setContinueEnabled(true);
          }

          // Store selection for later retrieval
          this.currentSelection = {
            stepNumber: this.currentStep,
            answerIndex: parseInt(selectedIndex, 10),
          };

          // Persist to global state
          this.saveAnswer(this.currentStep, parseInt(selectedIndex, 10));
        });
      });
    }

    // Disable continue until selection is made
    this.setContinueEnabled(false);

    // Restore previously saved answer for this step
    this.restoreAnswer(this.currentStep);
  }

  // Render the last step display (called when all quiz steps are answered)
  renderLastStep() {
    const template = document.getElementById("quiz-template-last");
    if (!template) return;

    this.isOnLastStepDisplay = true;

    // Template content is pre-populated by Liquid — just clone and append
    this.stepContent.innerHTML = "";
    this.stepContent.appendChild(template.content.cloneNode(true));

    // Update footer continue button text for last step
    const arrowRight = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="18" viewBox="0 0 32 18" fill="none">
      <path d="M25 10H7C6.44 10 6 9.56 6 9C6 8.44 6.44 8 7 8H25C25.56 8 26 8.44 26 9C26 9.56 25.56 10 25 10Z" fill="white"/>
      <path d="M20 17.0009C19.8688 17.0025 19.7388 16.9767 19.6183 16.925C19.4977 16.8733 19.3893 16.797 19.3 16.7009C18.9 16.3009 18.9 15.6809 19.3 15.2809L25.6 8.98094L19.3 2.68094C18.9 2.28094 18.9 1.66094 19.3 1.26094C19.7 0.860938 20.32 0.860938 20.72 1.26094L27.72 8.26094C28.12 8.66094 28.12 9.28094 27.72 9.68094L20.72 16.6809C20.52 16.8809 20.26 16.9809 20.02 16.9809L20 17.0009Z" fill="white"/>
    </svg>`;
    const cfg = window.quizLastStepSettings || {};
    const buttonLabel = cfg.buttonLabel || "Get your cat's meal plan";
    this.continueButton.innerHTML = `${buttonLabel} ${arrowRight}`;

    // Require a valid email before enabling the continue button
    const emailInput = this.stepContent.querySelector(
      ".quiz-last__email-input",
    );
    if (emailInput) {
      const validateEmail = () => {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          emailInput.value.trim(),
        );
        emailInput.classList.toggle(
          "quiz-last__email-input--error",
          emailInput.value.length > 0 && !isValid,
        );
        this.setContinueEnabled(isValid);
      };
      emailInput.addEventListener("input", validateEmail);
      validateEmail();
    } else {
      this.setContinueEnabled(true);
    }

    // Mark all breadcrumbs as completed
    this.breadcrumbs.forEach((breadcrumb) => {
      breadcrumb.classList.remove("quiz__breadcrumb--active");
      breadcrumb.classList.add("quiz__breadcrumb--completed");
    });
    this.breadcrumbLines.forEach((line) => {
      line.classList.add("quiz__breadcrumb-line--active");
    });

    this.saveProgress();
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const quizEl = document.querySelector(".quiz");
  if (quizEl) {
    window.quiz = new Quiz(quizEl);
  }
});

// Reinitialize when modal opens (in case DOM wasn't ready)
document.addEventListener("modal:open", (e) => {
  if (e.target.id !== "quiz-modal") return;
  if (!window.quiz) {
    const quizEl = document.querySelector(".quiz");
    if (quizEl) window.quiz = new Quiz(quizEl);
  } else {
    // Reopened after minimize — re-render current step to refresh UI
    if (window.quiz.isOnLastStepDisplay) {
      window.quiz.renderLastStep();
    } else if (window.quiz._waitlistShown) {
      // Waitlist was the last screen shown — reset so the user starts fresh
      // instead of landing back on a dead-end step where all pets are unsupported.
      window.quiz.resetQuiz();
    } else {
      window.quiz.goToStep(window.quiz.currentStep);
    }
  }
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", "quiz_open", { event_category: "quiz" });
  }
  if (typeof fbq === "function") {
    fbq("trackCustom", "QuizOpen");
  }
});
